/**
 * Pack-loader cache (E2.3.S2 generalisation) — one `AssetLoader` per asset-pack
 * id, cached templates shared across consumers, plus the three-side GPU disposer
 * for detached clones.
 *
 * This is the E2.2.S2 pack-loader acquisition (equipment renderer's `loaderFor` +
 * `defaultDispose`) lifted into a common module so the E2.3 enemy creature path
 * CONSUMES it rather than duplicating it (the E2.3.S2 dependency contract:
 * "consume its pack-loader acquisition … a frontend-local generalisation … is
 * in-scope; duplicating it is not"). The E2.2 equipment renderer + gather-tool
 * prop keep their own inline copies for now; folding them onto THIS cache is the
 * E2.4.S1 in-scope generalisation the E2.2.S5 gate already queued (follow-up
 * E22-F3) — this module is the ready target, deliberately not retrofitted here to
 * keep E2.3.S2 surgical (zero churn in proven E2.2 files).
 *
 * Node-safe: no runtime `three` dependency. Imports the sibling loader/manifest directly (not the barrel) to avoid
 * an import cycle once the barrel re-exports this module.
 */

import type { AssetPackDef } from '@great-cloud/shared/client';

import { AssetLoader, type GltfLoadFn } from './loader.js';
import { AssetManifest } from './manifest.js';

/**
 * Free a detached clone's OWN GPU resources, never the template's it shares —
 * the ownership rule lives in `./gpuOwnership.ts` (H-1349); re-exported here so
 * every existing consumer keeps its import.
 */
export { disposeObject3D } from './gpuOwnership.js';

export interface PackLoaderCacheOptions {
  /** Injected glTF loader (real: `createThreeGltfLoader()`; tests: a stub). */
  gltfLoad: GltfLoadFn;
  /** Static asset HTTP base for delivery URLs (`assetHttpBase()`); '' = same-origin. */
  httpBase?: string;
  /** Non-fatal warning sink passed to each `AssetLoader` (default = its own console.warn). */
  onWarn?: (message: string, error?: unknown) => void;
}

/**
 * One `AssetLoader` per pack id; the loader caches its own templates so many
 * instances of one pack asset share a single load. Construct once per consumer
 * (equipment renderer, creature renderer, …), `loaderFor(pack)` per attach, and
 * `clear()` on teardown / zone change.
 */
export class PackLoaderCache {
  private readonly loaders = new Map<string, AssetLoader>();
  private readonly gltfLoad: GltfLoadFn;
  private readonly httpBase: string;
  private readonly onWarn?: (message: string, error?: unknown) => void;

  constructor(options: PackLoaderCacheOptions) {
    this.gltfLoad = options.gltfLoad;
    this.httpBase = options.httpBase ?? '';
    this.onWarn = options.onWarn;
  }

  /** The loader for a pack (constructed once, then cached by pack id). */
  loaderFor(pack: AssetPackDef): AssetLoader {
    let loader = this.loaders.get(pack.id);
    if (loader === undefined) {
      loader = new AssetLoader(new AssetManifest(pack), {
        load: this.gltfLoad,
        httpBase: this.httpBase,
        onWarn: this.onWarn,
      });
      this.loaders.set(pack.id, loader);
    }
    return loader;
  }

  /** Drop every cached loader's templates + the cache itself (teardown/zone change). */
  clear(): void {
    for (const loader of this.loaders.values()) loader.clear();
    this.loaders.clear();
  }
}
